import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { PageHeader, Tab, Card, Typography, Button, Modal, TextInput, Spinner, EmptyComponent, DatePicker } from '../../components';
import { TextArea } from '../../components/TextArea';
import { adminAddCommunityEvent, adminRemoveCommunityEvent, adminRetrieveCommunityEvents, adminUpdateCommunityEvent } from '../../services/adminService';
import { META_TITLE } from '../../utils/constants';
import { required } from '../../utils/form-default-errors';
import moment from 'moment';
import * as S from './AdminCommunityEvents.styles';
import { renderArticleDateStatus } from '../../utils/time-formatter';
import { sortResourcesEvents } from '../../utils/sorting';

export default function AdminCommunityEvents() {
	const [isOpen, setIsOpen] = useState(false);
	const [removeIsOpen, setRemoveIsOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const [activeEvent, setActiveEvent] = useState(null);
	const [status, setStatus] = useState('loading');
	const [communityEvents, setCommunityEvents] = useState([]);

	const {
		handleSubmit,
		register,
		reset,
		setValue,
		watch,
		formState: { errors, isSubmitting },
	} = useForm();

	const startDate = watch('startDate');
	const endDate = watch('endDate');

	async function onSubmit(data) {
		try {
			if (isEditing) {
				const success = await adminUpdateCommunityEvent({ communityEventKey: activeEvent.CommunityEventKey, ...data });
				if (success) {
					toast.success('This community event has successfully been updated.');
					init();
					setIsOpen(false);
					setActiveEvent(null);
				} else {
					toast.error('There was an issue updating this community event. Please try again.');
				}
			} else {
				const success = await adminAddCommunityEvent({ ...data });
				if (success) {
					toast.success('This community event has successfully been created.');
					init();
					setIsOpen(false);
					setActiveEvent(null);
				} else {
					toast.error('There was an issue creating this community event. Please try again.');
				}
			}
		} catch (e) {
			toast.error(e.message);
			reset(undefined, { keepValues: true });
		}
	}

	const removeEvent = async () => {
		setIsRemoving(true);
		try {
			const success = await adminRemoveCommunityEvent({ communityEventKey: activeEvent.CommunityEventKey });
			setIsRemoving(false);
			if (success) {
				toast.success('This community event has successfully been removed.');
				const newEvents = communityEvents.filter((eventObj) => eventObj.CommunityEventKey !== activeEvent.CommunityEventKey);
				setCommunityEvents(newEvents);
				setRemoveIsOpen(false);
			} else {
				toast.error('There was an issue removing this community event. Please try again.');
			}
		} catch (e) {
			setIsRemoving(false);
			toast.error(e.message);
		}
	};

	const handleRemoveEvent = (eventObj) => {
		setActiveEvent(eventObj);
		setRemoveIsOpen(true);
	};

	const handleEditEvent = (eventObj) => {
		reset({ title: eventObj.Title, description: eventObj.Description, url: eventObj.URL, startDate: new Date(eventObj.StartDate), endDate: new Date(eventObj.EndDate) });
		setActiveEvent(eventObj);
		setIsEditing(true);
		setIsOpen(true);
	};

	const handleAddEvent = () => {
		reset({});
		setActiveEvent(null);
		setIsEditing(false);
		setIsOpen(true);
	};

	async function init() {
		try {
			const eventObjs = await adminRetrieveCommunityEvents({ returnExpired: true, returnCurrent: true, returnFuture: true });
			const now = new Date();
			eventObjs.sort(sortResourcesEvents);
			const fullEvents = eventObjs.map((eventObj) => {
				const endDate = new Date(eventObj.EndDate);
				if (endDate.getTime() < now.getTime()) eventObj.expired = true;
				return eventObj;
			});
			setCommunityEvents(fullEvents);
			setStatus('success');
		} catch (e) {
			setStatus('error');
		}
	}

	useEffect(() => {
		// Set document title
		document.title = `Community Events | ${META_TITLE}`;

		// Init
		init();
	}, []);

	const renderContent = () => {
		if (status === 'loading') {
			return <Spinner />;
		}
		if (communityEvents.length === 0) {
			return <EmptyComponent icon={['fal', 'calendar']} title="No Available Community Events" description="Create a new community event to get started." />;
		}
		return communityEvents.map((eventObj) => (
			<S.Item key={eventObj.CommunityEventKey}>
				<S.InfoWrapper expired={eventObj.expired}>
					<Typography tag="h4" weight="bold">
						{eventObj.Title}
					</Typography>
					<Typography tag="p">{eventObj.Description}</Typography>
					<S.Link href={eventObj.URL} target="_blank">
						{eventObj.URL}
					</S.Link>
					<Typography tag="p" variation="2" style={{ marginTop: '10px' }}>
						{renderArticleDateStatus(eventObj)}
					</Typography>
				</S.InfoWrapper>
				<S.ButtonWrapper>
					<Button variant="text" variation="warning" size="small" onClick={() => handleRemoveEvent(eventObj)}>
						<Typography tag="span" variation="2" weight="extrablack">
							Remove
						</Typography>
					</Button>
					<Button variant="outline" size="small" onClick={() => handleEditEvent(eventObj)}>
						<FontAwesomeIcon icon={['fal', 'edit']} />
						<Typography tag="span" variation="2" weight="extrablack">
							Edit
						</Typography>
					</Button>
				</S.ButtonWrapper>
			</S.Item>
		));
	};

	return (
		<S.Wrapper>
			<PageHeader>Community Events</PageHeader>
			<Card title="All Events" action={[{ id: 1, onClick: () => handleAddEvent(), icon: ['fal', 'plus'], label: 'Add New Event' }]}>
				<S.ContentWrapper>{renderContent()}</S.ContentWrapper>
			</Card>
			<Modal shouldCloseOnOverlayClick allowScroll isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
				<S.ModalWrapper>
					<S.TitleWrapper>
						<Typography tag="h1" weight="bold" center>
							{isEditing ? 'Update Community Event' : 'Add New Community Event'}
						</Typography>
					</S.TitleWrapper>
					<S.Form onSubmit={handleSubmit(onSubmit)}>
						<TextInput
							label="Title"
							id="title"
							error={errors.title}
							{...register('title', {
								required: required('Title'),
							})}
						/>
						<TextInput
							label="Link"
							id="url"
							error={errors.url}
							{...register('url', {
								required: required('Link'),
							})}
						/>
						<TextArea
							label="Description"
							id="description"
							error={errors.description}
							{...register('description', {
								required: required('Description'),
							})}
						/>
						<DatePicker
							{...register('startDate', {
								required: true,
							})}
							error={errors.startDate}
							label="Open Date"
							placeholderText="Select a Date"
							id="startDate"
							selected={startDate}
							onChange={(date) => setValue('startDate', date)}
						/>
						<DatePicker
							{...register('endDate', {
								required: true,
							})}
							error={errors.endDate}
							label="Close Date"
							placeholderText="Select a Date"
							id="endDate"
							selected={endDate}
							onChange={(date) => setValue('endDate', date)}
							minDate={startDate || new Date()}
						/>
						<S.FormButtonWrapper>
							<Button variant="outline" variation="secondary" type="button" onClick={() => setIsOpen(false)}>
								<Typography tag="span" variation="1" weight="extrablack">
									Cancel
								</Typography>
							</Button>
							<Button>
								<Typography tag="span" variation="1" weight="extrablack">
									{isSubmitting ? 'Loading...' : `${isEditing ? 'Update Community Event' : 'Add Community Event'}`}
								</Typography>
							</Button>
						</S.FormButtonWrapper>
					</S.Form>
				</S.ModalWrapper>
			</Modal>
			<Modal
				shouldCloseOnOverlayClick
				isOpen={removeIsOpen}
				onRequestClose={() => {
					setRemoveIsOpen(false);
				}}
			>
				<S.ModalWrapper>
					<S.TitleWrapper>
						<Typography tag="h1" weight="bold" center>
							Remove Community Event
						</Typography>
						<Typography tag="h3" center>
							Are you sure you want to remove this community event?
						</Typography>
					</S.TitleWrapper>
					<S.FormButtonWrapper>
						<Button
							variant="outline"
							variation="secondary"
							type="button"
							onClick={() => {
								setRemoveIsOpen(false);
							}}
						>
							<Typography tag="span" variation="1" weight="extrablack">
								Cancel
							</Typography>
						</Button>
						<Button variation="warning" onClick={removeEvent}>
							<Typography tag="span" variation="1" weight="extrablack">
								{isRemoving ? 'Loading...' : 'Remove Event'}
							</Typography>
						</Button>
					</S.FormButtonWrapper>
				</S.ModalWrapper>
			</Modal>
		</S.Wrapper>
	);
}
